import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LuX } from "react-icons/lu";
import { AdminInput, AdminTextarea } from "../../../../../components/adminForm";
import {
  createAttributeGroup,
  getAttributeFromGroup,
  updateAttributeGroup,
} from "../../../../../api/attributes";
import {
  createOptionGroup,
  getOptionGroups,
  updateOptionGroup,
} from "../../../../../api/options";
import {
  updateFloatPanel,
  requestTableRefresh,
} from "../../../../../reducers/userConfigSlice";
import { activeValue } from "../../../../../components/collectionTools";
import "../../products/edition/productEditorPage.css";
import { EditorDialog } from "../../products/edition/editorDialog";
import { AttributesList } from "./attributesList";
const draft = (row) => ({
  name: row?.name || "",
  mandatory: activeValue(row?.mandatory),
  multiple: row ? !activeValue(row.onlyOne) : false,
  maxItems: row?.maxItems ?? 0,
  description: row?.description || "",
});
const comparable = (row, type) =>
  type === "variant" ? { name: row.name } : draft(row);
export const AttributeEditor = () => {
  const panel = useSelector(
      (state) => state.aplicationConfig.userConfig.floatPanel,
    ),
    dispatch = useDispatch();
  const initialType = panel.data?.type === "variant" ? "variant" : "attribute";
  const [type, setType] = useState(initialType),
    [id, setId] = useState(panel.isEmpty ? null : panel.queryId);
  const [row, setRow] = useState(null),
    [form, setForm] = useState(null),
    [baseline, setBaseline] = useState(null),
    [values, setValues] = useState([]);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [saved, setSaved] = useState(false),
    [unknown, setUnknown] = useState(false),
    [retry, setRetry] = useState(0),
    [discard, setDiscard] = useState(false);
  const [managing, setManaging] = useState(false),
    [managerVersion, setManagerVersion] = useState(0);
  const [managerState, setManagerState] = useState({
    busy: false,
    dirty: false,
  });
  const lock = useRef(false),
    root = useRef(null),
    manager = useRef(null);
  const dirty =
    form &&
    JSON.stringify(type === "variant" ? { name: form.name } : form) !==
      JSON.stringify(type === "variant" ? { name: baseline.name } : baseline);
  const close = () => {
    if (lock.current) return;
    if (dirty) {
      setDiscard(true);
      return;
    }
    dispatch(
      updateFloatPanel({ visible: false, queryId: null, isEmpty: true }),
    );
  };
  const read = async (target, kind) => {
    if (kind === "variant") {
      const response = await getOptionGroups();
      const found =
        response?.error === false &&
        Array.isArray(response.data) &&
        response.data.find((g) => g.optionGroupId === target);
      if (!found || !Array.isArray(found.options)) throw Error();
      return { group: found, values: found.options };
    }
    const response = await getAttributeFromGroup(target);
    if (
      response?.error !== false ||
      response.data?.itemGroup?.itemGroupId !== target ||
      !Array.isArray(response.data.items)
    )
      throw Error();
    return {
      group: response.data.itemGroup,
      values: [...response.data.items].sort(
        (a, b) => (Number(a.order) || 0) - (Number(b.order) || 0),
      ),
    };
  };
  useEffect(() => {
    let current = true;
    setError("");
    if (panel.isEmpty) {
      const next = draft(null);
      setForm(next);
      setBaseline(next);
      setValues([]);
      return;
    }
    read(panel.queryId, initialType)
      .then((result) => {
        if (!current) return;
        setRow(result.group);
        setForm(draft(result.group));
        setBaseline(draft(result.group));
        setValues(result.values);
      })
      .catch(() => {
        if (current)
          setError("No se ha podido cargar el grupo. Vuelve a intentarlo.");
      });
    return () => {
      current = false;
    };
    // Type is fixed for existing groups; a new draft has no remote record to reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panel.queryId, panel.isEmpty, retry]);
  useEffect(() => {
    const warn = (e) => {
      if (
        dirty ||
        managerState.dirty ||
        managerState.busy ||
        manager.current?.querySelector(
          '[data-attribute-dirty="true"],[data-attribute-pending="true"]',
        )
      ) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, managerState]);
  const change = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  };
  const save = async (event) => {
    event.preventDefault();
    if (lock.current || unknown || !form || managing) return;
    if (!form.name.trim()) {
      setError("Escribe el nombre del grupo.");
      root.current.querySelector("#attribute-editor-name")?.focus();
      return;
    }
    if (
      type === "attribute" &&
      (!Number.isInteger(Number(form.maxItems)) || Number(form.maxItems) < 0)
    ) {
      setError(
        "El máximo de valores debe ser un número entero positivo o cero.",
      );
      return;
    }
    lock.current = true;
    setBusy(true);
    setError("");
    setSaved(false);
    let target = id;
    const expected = {
      ...form,
      name: form.name.trim(),
      maxItems: Number(form.maxItems),
    };
    try {
      const payload =
        type === "variant"
          ? { name: expected.name, isMainGroup: activeValue(row?.isMainGroup) }
          : {
              name: expected.name,
              description: expected.description,
              onlyOne: !expected.multiple,
              mandatory: expected.mandatory,
              maxItems: expected.maxItems,
              isActive: row ? activeValue(row.isActive) : true,
              order: row?.order ?? 0,
            };
      if (!target) {
        setUnknown(true);
        const response =
          type === "variant"
            ? await createOptionGroup(payload)
            : await createAttributeGroup(payload);
        target =
          type === "variant"
            ? response?.data?.optionGroupId
            : response?.data?.itemGroup?.itemGroupId;
        if (response?.error !== false || !target)
          throw Error(
            "No se ha podido confirmar la creación. Comprueba el listado antes de repetirla.",
          );
        setId(target);
        setUnknown(false);
      } else {
        const response =
          type === "variant"
            ? await updateOptionGroup(payload, target)
            : await updateAttributeGroup(target, payload);
        if (response?.error !== false)
          throw Error(
            "No se ha podido guardar el grupo. Tus cambios se conservan.",
          );
      }
      const confirmed = await read(target, type);
      // Creation normalizes names to lowercase; updates must preserve the requested name.
      const actual = comparable(confirmed.group, type),
        wanted = type === "variant" ? { name: expected.name } : expected;
      if (!id) {
        wanted.name = wanted.name.toLowerCase();
      }
      if (JSON.stringify(actual) !== JSON.stringify(wanted))
        throw Error(
          "No se ha podido confirmar el guardado. Tus cambios se conservan; vuelve a comprobarlo.",
        );
      setRow(confirmed.group);
      setForm(draft(confirmed.group));
      setBaseline(draft(confirmed.group));
      setValues(confirmed.values);
      setSaved(true);
      dispatch(requestTableRefresh());
      if (!id)
        dispatch(
          updateFloatPanel({
            visible: true,
            isEmpty: false,
            queryId: target,
            data: { ...confirmed.group, type },
          }),
        );
    } catch (e) {
      setError(
        !target
          ? "No se ha podido confirmar la creación. Comprueba el listado antes de repetirla."
          : e.message ||
              "No se ha podido guardar el grupo. Tus cambios se conservan.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const toggleManager = async () => {
    if (!managing) {
      setManaging(true);
      return;
    }
    if (
      managerState.busy ||
      managerState.dirty ||
      manager.current?.querySelector(
        '[data-attribute-dirty="true"],[data-attribute-pending="true"]',
      )
    ) {
      setError(
        "Guarda los cambios y el orden de los valores antes de cerrar su gestor.",
      );
      return;
    }
    try {
      const result = await read(id, type);
      setValues(result.values);
      setManagerVersion((n) => n + 1);
      setManaging(false);
      setError("");
      dispatch(requestTableRefresh());
    } catch {
      setError(
        "No se han podido actualizar los valores. Vuelve a comprobarlo.",
      );
    }
  };
  return (
    <section
      className="attributeReferenceEditor"
      ref={root}
      aria-busy={busy}
      onKeyDownCapture={(event) => {
        if (event.key === "Escape" && !discard) {
          event.preventDefault();
          event.stopPropagation();
          if (managing) return;
          close();
        }
      }}
    >
      <header className="attributeEditorHeading">
        <div>
          <h2 id="admin-editor-title">
            {id ? `Editar · ${row?.name || "Grupo"}` : "Nuevo atributo"}
          </h2>
          <p>Variantes y atributos reutilizables.</p>
        </div>
        <button
          type="button"
          aria-label="Cerrar"
          disabled={busy || managing}
          onClick={close}
        >
          <LuX />
        </button>
      </header>
      <form onSubmit={save} className="attributeEditorForm">
        <div className="attributeEditorBody">
          {error && (
            <div role="alert" className="attributeEditorError">
              {error}
              {!form && (
                <button type="button" onClick={() => setRetry((n) => n + 1)}>
                  Reintentar
                </button>
              )}
            </div>
          )}
          {unknown && (
            <p role="status">
              Vuelve al listado y abre el grupo para comprobar el resultado
              antes de repetir la creación.
            </p>
          )}
          {!form ? (
            !error && <p role="status">Cargando grupo…</p>
          ) : (
            <fieldset
              disabled={busy || unknown || managing}
              className="attributeEditorFields"
            >
              <div className="attributeEditorIdentity">
                <AdminInput
                  id="attribute-editor-name"
                  label="Nombre"
                  required
                  value={form.name}
                  onChange={(value) => change("name", value)}
                />
                <fieldset className="attributeEditorType" disabled={!!id}>
                  <legend>Tipo</legend>
                  <div>
                    <label>
                      <input
                        type="radio"
                        name="attribute-type"
                        checked={type === "variant"}
                        onChange={() => {
                          setType("variant");
                          setSaved(false);
                        }}
                      />
                      Variante
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="attribute-type"
                        checked={type === "attribute"}
                        onChange={() => {
                          setType("attribute");
                          setSaved(false);
                        }}
                      />
                      Atributo
                    </label>
                  </div>
                  <p>Las variantes se muestran antes que los atributos.</p>
                </fieldset>
              </div>
              <AdminInput
                id="attribute-editor-values"
                label="Valores"
                readOnly
                value={values.map((v) => v.name).join(" · ")}
                placeholder="Sin valores"
                description={
                  type === "attribute" && !id
                    ? "Guarda el atributo para añadir sus valores."
                    : undefined
                }
              />
              <label className="attributeRequired">
                <input
                  type="checkbox"
                  checked={type === "attribute" && form.mandatory}
                  disabled={type === "variant"}
                  onChange={(e) => change("mandatory", e.target.checked)}
                />
                Obligatorio al añadir el producto
              </label>
              <label className="attributeMultiple">
                <span>Permitir varios valores</span>
                <input
                  type="checkbox"
                  role="switch"
                  checked={type === "attribute" && form.multiple}
                  disabled={type === "variant"}
                  onChange={(e) => change("multiple", e.target.checked)}
                />
              </label>
              {type === "variant" && (
                <details className="attributeAdvanced">
                  <summary>Más opciones</summary>
                  <p>Los valores de las variantes se gestionan desde las opciones de un producto.</p>
                </details>
              )}
              {type === "attribute" && (
                <details className="attributeAdvanced">
                  <summary>Más opciones</summary>
                  <AdminTextarea
                    id="attribute-editor-description"
                    label="Descripción"
                    rows={3}
                    value={form.description}
                    onChange={(e) => change("description", e.target.value)}
                  />
                  <AdminInput
                    id="attribute-editor-max"
                    type="number"
                    min="0"
                    step="1"
                    label="Máximo de valores"
                    description="Máximo de atributos seleccionables en este grupo."
                    value={form.maxItems}
                    onChange={(value) => change("maxItems", value)}
                  />
                </details>
              )}
            </fieldset>
          )}
          {id && type === "attribute" && panel.queryId === id && (
            <div className="attributeValuesManager">
              <button
                type="button"
                disabled={
                  busy ||
                  unknown ||
                  dirty ||
                  managerState.busy ||
                  managerState.dirty
                }
                onClick={toggleManager}
              >
                {managing ? "Cerrar gestor de valores" : "Gestionar valores"}
              </button>
              {dirty && !managing && (
                <p>
                  Guarda los cambios del grupo antes de gestionar sus valores.
                </p>
              )}
              {managing && (
                <div ref={manager}>
                  <AttributesList
                    key={managerVersion}
                    onEditorStateChange={setManagerState}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <footer className="attributeEditorFooter">
          <span role="status">
            {saved ? "Cambios guardados" : dirty ? "Cambios sin guardar" : ""}
          </span>
          <button type="button" disabled={busy || managing} onClick={close}>
            Cancelar
          </button>
          <button
            className="attributePrimary"
            disabled={!form || busy || unknown || managing || (!dirty && !!id)}
          >
            {busy
              ? "Guardando…"
              : type === "variant"
                ? "Guardar variante"
                : "Guardar atributo"}
          </button>
        </footer>
      </form>
      {discard && (
        <EditorDialog
          title="Descartar cambios"
          description="Hay cambios sin guardar en este grupo."
          onClose={() => setDiscard(false)}
          footer={
            <>
              <button type="button" onClick={() => setDiscard(false)}>
                Seguir editando
              </button>
              <button
                type="button"
                onClick={() =>
                  dispatch(
                    updateFloatPanel({
                      visible: false,
                      queryId: null,
                      isEmpty: true,
                    }),
                  )
                }
              >
                Descartar y volver
              </button>
            </>
          }
        />
      )}
    </section>
  );
};
